/**
 * The first-party device id (spec section 7, "Abuse limits"). One random 128-bit id, kept in three places so that
 * clearing one or two of them does not reset the free-trial and sign-up limits:
 *   1. the cookie `fj_dev` on this site (host-only, not the API's HttpOnly cookie),
 *   2. localStorage item `fj_dev`,
 *   3. IndexedDB database `fjalim`, store `device`, key `fj_dev`.
 * On load the first one present wins and is written back to all three. When none exists a new random id is made.
 * It is not a fingerprint: nothing is read from the canvas, fonts or hardware, and a browser cannot read a MAC address.
 * The API stores only a salted hash of it. The same logic lives in apps/site/src/lib/device.ts; keep them in step.
 */
export const DEVICE_STORAGE_KEY = 'fj_dev'
export const DEVICE_COOKIE = 'fj_dev'
export const DEVICE_HEADER = 'X-Fjalim-Device'
export const DEVICE_IDB_NAME = 'fjalim'
export const DEVICE_IDB_STORE = 'device'
const COOKIE_MAX_AGE = 365 * 86400

/** The same shape the API accepts: 16 random bytes in base64url are 22 characters. */
const SHAPE = /^[A-Za-z0-9_-]{22,64}$/

export const isDeviceId = (value: unknown): value is string => typeof value === 'string' && SHAPE.test(value)

/** One place the id can live. Both methods may throw or reject (blocked storage); callers never see that. */
export interface DeviceStore {
  read(): string | undefined | Promise<string | undefined>
  write(id: string): void | Promise<void>
}

/** A new random id: 16 bytes from the browser's CSPRNG as 22 base64url characters. */
export function newDeviceId(random: (bytes: Uint8Array) => Uint8Array = (b) => crypto.getRandomValues(b)): string {
  const bytes = random(new Uint8Array(16))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

async function safeRead(store: DeviceStore): Promise<string | undefined> {
  try {
    const value = await store.read()
    return isDeviceId(value) ? value : undefined
  } catch {
    return undefined
  }
}

async function writeAll(stores: readonly DeviceStore[], id: string): Promise<void> {
  // One blocked store must not stop the others.
  await Promise.allSettled(stores.map(async (store) => store.write(id)))
}

/**
 * The id to send. Reads the stores in order and takes the first valid one, then writes it to every store, so a
 * store that was cleared is filled again. With none present it makes a new id and writes that. Never throws.
 */
export async function loadDeviceId(stores: readonly DeviceStore[], make: () => string = newDeviceId): Promise<string> {
  let id: string | undefined
  for (const store of stores) {
    id = await safeRead(store)
    if (id) break
  }
  id ??= make()
  await writeAll(stores, id)
  return id
}

/** Writes an id the API returned (it may differ from ours when the API's own cookie won) to every store. Ignores a malformed id. */
export async function saveDeviceId(stores: readonly DeviceStore[], id: string | undefined): Promise<void> {
  if (isDeviceId(id)) await writeAll(stores, id)
}

/* ---- Browser stores ----------------------------------------------------- */

export function cookieStore(doc: { cookie: string }, secure: boolean): DeviceStore {
  return {
    read() {
      for (const part of doc.cookie.split(';')) {
        const [name, ...rest] = part.trim().split('=')
        if (name === DEVICE_COOKIE) return rest.join('=')
      }
      return undefined
    },
    write(id) {
      doc.cookie = `${DEVICE_COOKIE}=${id}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
    },
  }
}

export function localStore(storage: Pick<Storage, 'getItem' | 'setItem'>): DeviceStore {
  return {
    read: () => storage.getItem(DEVICE_STORAGE_KEY) ?? undefined,
    write: (id) => storage.setItem(DEVICE_STORAGE_KEY, id),
  }
}

export function indexedDbStore(factory: IDBFactory): DeviceStore {
  const open = () =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(DEVICE_IDB_NAME, 1)
      request.onupgradeneeded = () => request.result.createObjectStore(DEVICE_IDB_STORE)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
      request.onblocked = () => reject(new Error('blocked'))
    })
  return {
    async read() {
      const db = await open()
      try {
        return await new Promise<string | undefined>((resolve, reject) => {
          const request = db.transaction(DEVICE_IDB_STORE, 'readonly').objectStore(DEVICE_IDB_STORE).get(DEVICE_STORAGE_KEY)
          request.onsuccess = () => resolve(typeof request.result === 'string' ? request.result : undefined)
          request.onerror = () => reject(request.error)
        })
      } finally {
        db.close()
      }
    },
    async write(id) {
      const db = await open()
      try {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(DEVICE_IDB_STORE, 'readwrite')
          tx.objectStore(DEVICE_IDB_STORE).put(id, DEVICE_STORAGE_KEY)
          tx.oncomplete = () => resolve()
          tx.onerror = () => reject(tx.error)
          tx.onabort = () => reject(tx.error)
        })
      } finally {
        db.close()
      }
    },
  }
}

interface BrowserLike {
  document?: { cookie: string }
  location?: { protocol: string }
  localStorage?: Pick<Storage, 'getItem' | 'setItem'>
  indexedDB?: IDBFactory
}

/**
 * The three stores of this browser. Reaching `window.localStorage` can throw when storage is blocked, so each
 * accessor is guarded; a store that cannot be built is simply left out.
 */
export function browserDeviceStores(win: BrowserLike): DeviceStore[] {
  const stores: DeviceStore[] = []
  const add = (build: () => DeviceStore | undefined) => {
    try {
      const store = build()
      if (store) stores.push(store)
    } catch {
      /* blocked: the other stores still work */
    }
  }
  add(() => (win.document ? cookieStore(win.document, win.location?.protocol === 'https:') : undefined))
  add(() => (win.localStorage ? localStore(win.localStorage) : undefined))
  add(() => (win.indexedDB ? indexedDbStore(win.indexedDB) : undefined))
  return stores
}

let current: Promise<string> | undefined

/**
 * The id for this page load, loaded (and written back to all three stores) once. The sign-up form awaits it
 * and sends it as `X-Fjalim-Device`.
 */
export function getDeviceId(): Promise<string> {
  current ??= loadDeviceId(browserDeviceStores(window))
  return current
}

/** Test seam: forget the loaded id so the next call reads the stores again. */
export function resetDeviceId(): void {
  current = undefined
}

/**
 * Adopts the id the API accepted (its `fj_dev` cookie may have won over ours): writes it to the cookie, localStorage
 * and IndexedDB, and makes it the id for this page load. Ignores a missing or malformed id. Never throws.
 */
export async function rememberDeviceId(id: string | undefined): Promise<void> {
  if (!isDeviceId(id)) return
  current = Promise.resolve(id)
  await saveDeviceId(browserDeviceStores(window), id)
}
